import { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Search, Check, Clock, CalendarDays, AlertTriangle,
  Droplets, Syringe, CircleDot, Bandage, PlusCircle,
  Send, ChevronDown, Info, User, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { pazientiInCaricoMMG } from '@/data/mockMMGData';

/* ══════ PRESTAZIONI QUICK-ACTION ══════ */
const prestazioni = [
  { id: 'prelievo', label: 'Prelievo Ematico', icon: Droplets, color: '#EF4444', bg: '#FEF2F2' },
  { id: 'catetere', label: 'Sostituzione Catetere Vescicale', icon: CircleDot, color: '#8B5CF6', bg: '#F5F3FF' },
  { id: 'iniettiva', label: 'Terapia Iniettiva / Infusionale', icon: Syringe, color: '#3B82F6', bg: '#EFF6FF' },
  { id: 'medicazione', label: 'Medicazione Semplice', icon: Bandage, color: '#F59E0B', bg: '#FFFBEB' },
  { id: 'altro', label: 'Altro', icon: PlusCircle, color: '#6B7280', bg: '#F3F4F6' },
];

/* ══════ COMBOBOX PAZIENTE ══════ */
function PatientCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  const attivi = pazientiInCaricoMMG.filter(p => p.stato === 'attivo' || p.stato === 'in-scadenza');

  const filtered = useMemo(() => {
    if (!query.trim()) return attivi;
    const q = query.toLowerCase();
    return attivi.filter(p => p.nome.toLowerCase().includes(q) || p.cf.toLowerCase().includes(q));
  }, [query, attivi]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = value ? pazientiInCaricoMMG.find(p => p.id === value) : null;

  return (
    <div className="relative" ref={ref} data-testid="patient-combobox">
      <div
        className={`flex items-center gap-3 w-full h-14 px-4 rounded-2xl border-2 transition-all cursor-pointer ${open ? 'border-primary/40 ring-4 ring-primary/10 bg-card' : 'border-border/40 bg-card hover:border-border/60'}`}
        onClick={() => setOpen(true)}
        data-testid="patient-combobox-trigger"
      >
        <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        {selected && !open ? (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-slate-500 dark:text-slate-300">
                {selected.nome.split(' ').map(w => w[0]).join('')}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{selected.nome}</p>
              <p className="text-[10px] text-muted-foreground">{selected.diagnosi}</p>
            </div>
            <Badge variant="outline" className="text-[9px] ml-auto flex-shrink-0">{selected.cia}</Badge>
          </div>
        ) : (
          <input
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            placeholder="Cerca paziente per nome o codice fiscale..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
            autoFocus={open}
            data-testid="patient-search-input"
          />
        )}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-card border border-border/40 rounded-2xl shadow-lg overflow-hidden"
            data-testid="patient-dropdown"
          >
            {open && !selected && (
              <div className="px-4 py-2 border-b border-border/20">
                <input
                  className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                  placeholder="Cerca paziente..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
              </div>
            )}
            <div className="max-h-64 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Nessun paziente trovato.</p>
              ) : (
                filtered.map((p) => {
                  const initials = p.nome.split(' ').map(w => w[0]).join('');
                  const isSelected = value === p.id;
                  return (
                    <button
                      key={p.id}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}
                      onClick={() => { onChange(p.id); setOpen(false); setQuery(''); }}
                      data-testid={`patient-option-${p.id}`}
                    >
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-300">{initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{p.nome}, {p.eta} anni</p>
                        <p className="text-[10px] text-muted-foreground truncate">{p.diagnosi}</p>
                      </div>
                      <Badge variant="outline" className="text-[9px] flex-shrink-0">{p.cia}</Badge>
                      {isSelected && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ══════ GRIGLIA PRESTAZIONI ══════ */
function PrestazioneGrid({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="prestazione-grid">
      {prestazioni.map((p) => {
        const Icon = p.icon;
        const active = value === p.id;
        return (
          <motion.button
            key={p.id}
            whileTap={{ scale: 0.97 }}
            onClick={() => onChange(p.id)}
            className={`relative flex flex-col items-center justify-center gap-3 p-5 rounded-2xl border-2 transition-all cursor-pointer min-h-[130px] ${active ? 'border-primary shadow-md bg-primary/[0.04]' : 'border-border/30 bg-card hover:border-border/50 hover:shadow-sm'}`}
            data-testid={`prestazione-${p.id}`}
          >
            {active && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center"
              >
                <Check className="w-3 h-3 text-white" />
              </motion.div>
            )}
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform"
              style={{ backgroundColor: active ? `${p.color}18` : p.bg }}
            >
              <Icon className="w-6 h-6" style={{ color: p.color }} />
            </div>
            <span className={`text-xs font-semibold text-center leading-tight ${active ? 'text-primary' : 'text-foreground'}`}>
              {p.label}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
}

/* ══════ PAGINA PRINCIPALE ══════ */
export default function MMGQuickPrescription() {
  const [pazienteId, setPazienteId] = useState(null);
  const [prestazione, setPrestazione] = useState(null);
  const [priorita, setPriorita] = useState('routine');
  const [dataDesiderata, setDataDesiderata] = useState('');
  const [note, setNote] = useState('');
  const [inviato, setInviato] = useState(false);

  const paziente = pazienteId ? pazientiInCaricoMMG.find(p => p.id === pazienteId) : null;
  const prestazioneLabel = prestazione ? prestazioni.find(p => p.id === prestazione)?.label : '';

  const canSubmit = pazienteId && prestazione && note.trim().length >= 5;

  const handleSubmit = () => {
    setInviato(true);
    toast.success('Prescrizione inviata', {
      description: `${prestazioneLabel} per ${paziente?.nome} — ${priorita === 'urgente' ? 'Urgente (entro 24h)' : 'Routine (entro 48-72h)'}`,
    });
  };

  const handleReset = () => {
    setPazienteId(null);
    setPrestazione(null);
    setPriorita('routine');
    setDataDesiderata('');
    setNote('');
    setInviato(false);
  };

  if (inviato) {
    return (
      <AppLayout role="mmg">
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="prescrizione-inviata">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
            <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl max-w-lg w-full">
              <CardContent className="p-10 flex flex-col items-center text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-5"
                >
                  <Check className="w-8 h-8 text-emerald-500" />
                </motion.div>
                <h2 className="text-lg font-semibold text-foreground">Prescrizione Inviata</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-sm">
                  La richiesta di <strong>{prestazioneLabel}</strong> per <strong>{paziente?.nome}</strong> e stata inviata
                  all'erogatore {paziente?.provider || 'assegnato'}.
                </p>
                <div className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-100">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-amber-700">
                    {priorita === 'urgente' ? 'Prevista erogazione entro 24 ore' : 'Prevista erogazione entro 48-72 ore'}
                  </span>
                </div>
                <Button
                  className="mt-6 gap-2 rounded-xl h-11 px-6"
                  onClick={handleReset}
                  data-testid="btn-nuova-prescrizione"
                >
                  <PlusCircle className="w-4 h-4" />Nuova Prescrizione
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout role="mmg">
      <div className="space-y-6" data-testid="mmg-quick-prescription">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground" data-testid="page-title">Prescrizioni Rapide</h1>
              <p className="text-sm text-muted-foreground">Fast-Track — Interventi puntuali per pazienti in carico ADI</p>
            </div>
          </div>
        </div>

        {/* Step 1: Selezione Paziente */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="section-paziente">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-[10px] font-bold text-primary">1</span>
              </div>
              <h2 className="text-sm font-semibold text-foreground">Seleziona Paziente</h2>
            </div>
            <PatientCombobox value={pazienteId} onChange={setPazienteId} />

            {paziente && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 flex items-center gap-4 px-4 py-3 rounded-xl bg-muted/20 border border-border/20"
                data-testid="selected-patient-info"
              >
                <User className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{paziente.nome}</span> · {paziente.eta} anni · {paziente.cf}
                  {paziente.provider && <span className="ml-2 text-[10px]">· Provider: {paziente.provider}</span>}
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Selezione Prestazione */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="section-prestazione">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-[10px] font-bold text-primary">2</span>
              </div>
              <h2 className="text-sm font-semibold text-foreground">Tipo di Intervento</h2>
            </div>
            <PrestazioneGrid value={prestazione} onChange={setPrestazione} />
          </CardContent>
        </Card>

        {/* Step 3: Dettagli */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="section-dettagli">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-[10px] font-bold text-primary">3</span>
              </div>
              <h2 className="text-sm font-semibold text-foreground">Dettagli e Tempistiche</h2>
            </div>

            {/* Priorità */}
            <div>
              <label className="text-xs font-semibold text-foreground mb-2.5 block">Priorita</label>
              <div className="flex gap-3" data-testid="priorita-selector">
                <button
                  onClick={() => setPriorita('routine')}
                  className={`flex-1 flex items-center justify-center gap-2.5 p-4 rounded-2xl border-2 transition-all ${priorita === 'routine' ? 'border-blue-400 bg-blue-50/50 shadow-sm' : 'border-border/30 bg-card hover:border-border/50'}`}
                  data-testid="priorita-routine"
                >
                  <Clock className={`w-5 h-5 ${priorita === 'routine' ? 'text-blue-500' : 'text-muted-foreground'}`} />
                  <div className="text-left">
                    <p className={`text-sm font-semibold ${priorita === 'routine' ? 'text-blue-700' : 'text-foreground'}`}>Routine</p>
                    <p className="text-[10px] text-muted-foreground">Entro 48-72 ore</p>
                  </div>
                  {priorita === 'routine' && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </motion.div>
                  )}
                </button>
                <button
                  onClick={() => setPriorita('urgente')}
                  className={`flex-1 flex items-center justify-center gap-2.5 p-4 rounded-2xl border-2 transition-all ${priorita === 'urgente' ? 'border-red-400 bg-red-50/50 shadow-sm' : 'border-border/30 bg-card hover:border-border/50'}`}
                  data-testid="priorita-urgente"
                >
                  <AlertTriangle className={`w-5 h-5 ${priorita === 'urgente' ? 'text-red-500' : 'text-muted-foreground'}`} />
                  <div className="text-left">
                    <p className={`text-sm font-semibold ${priorita === 'urgente' ? 'text-red-700' : 'text-foreground'}`}>Urgente</p>
                    <p className="text-[10px] text-muted-foreground">Entro 24 ore</p>
                  </div>
                  {priorita === 'urgente' && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </motion.div>
                  )}
                </button>
              </div>
            </div>

            {/* Data desiderata */}
            <div>
              <label className="text-xs font-semibold text-foreground mb-2 block">
                Data Desiderata <span className="text-muted-foreground font-normal">(opzionale)</span>
              </label>
              <div className="relative max-w-xs">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="date"
                  className="w-full h-11 pl-10 pr-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={dataDesiderata}
                  onChange={(e) => setDataDesiderata(e.target.value)}
                  data-testid="data-desiderata-input"
                />
              </div>
            </div>

            {/* Note */}
            <div>
              <label className="text-xs font-semibold text-foreground mb-2 block">
                Note Mediche Specifiche <span className="text-red-400">*</span>
              </label>
              <textarea
                className="w-full h-28 rounded-xl border border-border bg-card p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Specificare tipo di esami (es. Emocromo, Creatinina, Glicemia) o farmaci da somministrare, posologia e via di somministrazione..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="note-mediche-textarea"
              />
              {note.trim().length > 0 && note.trim().length < 5 && (
                <p className="text-[10px] text-red-400 mt-1">Inserire almeno 5 caratteri.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Azione di invio */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="section-invio">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  L'intervento verra aggiunto al PAI corrente del paziente senza necessita di rivalutazione UVMD.
                  L'erogatore assegnato ricevera la notifica immediata.
                </p>
              </div>
              <Button
                className="gap-2.5 rounded-xl h-12 px-8 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-sm whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={!canSubmit}
                onClick={handleSubmit}
                data-testid="btn-prescrivi-invia"
              >
                <Send className="w-4 h-4" />Prescrivi e Invia all'Erogatore
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
